import type { Ref } from "react";
import classNames from "classnames";
import type { IconType } from "@circle-react-shared/Icon";
import { Icon } from "@circle-react-shared/Icon";
import { useToolbarContext } from "../Context";
import { ToolbarTooltip } from "../Tooltip";

export interface ToolbarButtonProps {
  active?: boolean;
  children?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  forwardedRef?: Ref<HTMLButtonElement>;
  icon: IconType;
  label?: string;
  onClick?: () => void;
  primary?: boolean;
  showIndicator?: boolean;
  tooltipText?: string;
}

export const ToolbarButton = ({
  icon,
  active,
  danger,
  primary,
  disabled,
  showIndicator,
  label,
  tooltipText,
  onClick,
  forwardedRef,
  children,
}: ToolbarButtonProps) => {
  const { theme } = useToolbarContext();
  const isAutoWidth = Boolean(label) || Boolean(children);
  const isDarkButtons = theme === "dark-buttons";
  const baseClasses = isDarkButtons
    ? "bg-black hover:bg-black/80"
    : "bg-white/5 hover:bg-white/10";
  return (
    <ToolbarTooltip content={tooltipText} disabled={disabled}>
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        ref={forwardedRef}
        className={classNames(
          "box-border flex h-11 items-center justify-center gap-1 rounded-2xl border border-transparent p-2.5 text-[#F0F3F5] hover:border-[#545861]",
          {
            [baseClasses]: !active && !danger && !primary,
            "bg-white/20": active && !danger && !primary,
            "bg-[#2C2F36]/80":
              active && !danger && !primary && theme === "dark-buttons",
            "bg-white/5 text-red-500 hover:border-red-500": !active && danger,
            "bg-[#E02F2F]": active && danger,
            "border-none opacity-40": disabled,
            "w-11 p-2.5": !isAutoWidth,
            "w-auto px-3 py-2.5": isAutoWidth,
            "bg-[#18AB4A] hover:bg-[#18AB4A]/80": primary,
          },
        )}
      >
        <div className="relative">
          <Icon type={icon} size={24} className="!h-6 !w-6" />
          {showIndicator && (
            <div className="absolute right-[-2px] top-0 flex h-[11px] w-[11px] text-[#506CF0]">
              <Icon type="12-indicator" size={12} />
            </div>
          )}
        </div>
        {label && <div className="text-xs-plus font-semibold">{label}</div>}
        {children}
      </button>
    </ToolbarTooltip>
  );
};
